import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import GridViewIcon from '@mui/icons-material/GridView';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import PersonIcon from '@mui/icons-material/Person';
import { useAssignees, useHasFilters } from 'hooks/common';
import { useTeams } from 'components/common/QuickTicketCreationForm/hooks';
import {
  togglePinnedFiltersState,
  selectGenericTicketsPinnedFilters,
} from '@store';
import {
  GENERIC_TICKETS_STATUS_OPTIONS,
  GENERIC_TICKET_PRIORITY_OPTIONS,
  GENERIC_TICKET_REASON_OPTIONS,
} from 'components/genericTickets/constants';
import { toCreatedByOptions } from './utils';

export const useGenericTicketsFilterConfig = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { assigneesWithNone, assignees } = useAssignees();
  const { activeTeams: teams } = useTeams(false);
  const pinnedFilters = useSelector(selectGenericTicketsPinnedFilters);

  const currentFilters = useMemo(() => {
    const parseQueryValue = (value) => {
      if (!value) return [];
      if (typeof value === 'string') {
        return value.split(',').filter(Boolean);
      }
      return Array.isArray(value) ? value : [];
    };

    return {
      team: parseQueryValue(router.query?.team),
      status: parseQueryValue(router.query?.status),
      assignee: router.query?.assignee || null,
      dueDate: router.query?.dueDate || null,
      priority: parseQueryValue(router.query?.priority),
      reason: parseQueryValue(router.query?.reason),
      createdBy: parseQueryValue(router.query?.createdBy),
      createdAt: {
        dateFrom: router.query?.dateFrom || null,
        dateTo: router.query?.dateTo || null,
      },
    };
  }, [router.query]);

  const filterCategories = useMemo(
    () => [
      {
        id: 'status',
        label: 'Status',
        icon: GridViewIcon,
        options: GENERIC_TICKETS_STATUS_OPTIONS,
        multiSelect: true,
      },
      {
        id: 'team',
        label: 'Team',
        icon: GridViewIcon,
        options: teams,
        multiSelect: true,
      },
      {
        id: 'assignee',
        label: 'Assignee',
        icon: RadioButtonUncheckedIcon,
        options: assigneesWithNone,
        multiSelect: false,
      },
      {
        id: 'dueDate',
        label: 'Due Date & time',
        icon: CalendarTodayIcon,
        type: 'dateTime',
        ampm: false,
        clockMaxHeight: '300px',
      },
      {
        id: 'priority',
        label: 'Priority',
        icon: PriorityHighIcon,
        options: GENERIC_TICKET_PRIORITY_OPTIONS,
        multiSelect: true,
      },
      {
        id: 'reason',
        label: 'Ticket Reason',
        icon: TextSnippetIcon,
        options: GENERIC_TICKET_REASON_OPTIONS,
        multiSelect: true,
      },
      {
        id: 'createdBy',
        label: 'Created By',
        icon: PersonIcon,
        options: toCreatedByOptions(assignees),
        multiSelect: true,
      },
      {
        id: 'createdAt',
        label: 'Created At',
        icon: CalendarTodayIcon,
        type: 'dateTimeRange',
        dateOnly: true,
        useISOFormat: true,
      },
    ],
    [teams, assigneesWithNone, assignees]
  );

  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(
      togglePinnedFiltersState({
        board: 'genericTickets',
        pinnedFilter,
      })
    );
  };

  const { hasFilters } = useHasFilters({
    pinnedFilters,
  });

  return {
    filterCategories,
    currentFilters,
    handlePinnedFilterSelect,
    pinnedFilters,
    supportPinnedFilters: true,
    hasFilters,
  };
};

export default useGenericTicketsFilterConfig;
